import React, { useState } from 'react';
import { toast } from 'react-toastify';
import type { ToastOptions } from 'react-toastify';
import classnames from 'classnames';
import IconSuccess from '@scramble/design-system/icons/check-circle.svg?react';
import IconDanger from '@scramble/design-system/icons/alert-circle.svg?react';
import IconWarning from '@scramble/design-system/icons/alert-triangle.svg?react';
import IconInfo from '@scramble/design-system/icons/info-circle.svg?react';
import IconNeutral from '@scramble/design-system/icons/bookmark.svg?react';
import type { SnackbarContentProps, SnackbarParams, SnackbarSupportParams, SnackbarType } from './types';
import s from './index.module.scss';

const DEFAULT_ICONS: Partial<Record<SnackbarType, React.FC<React.SVGProps<SVGSVGElement>>>> = {
  neutral: IconNeutral,
  success: IconSuccess,
  danger: IconDanger,
  warning: IconWarning,
  info: IconInfo,
};

export const toastDefaultConfig: ToastOptions = {
  autoClose: 3000,
  closeButton: false,
  hideProgressBar: true,
};

const getPosition = (): Pick<ToastOptions, 'position'> => ({
  position: window.innerWidth < 798 ? 'bottom-center' : 'bottom-center',
});

const SnackbarContent = ({ text, type, icon, actionLabel, actionClick }: SnackbarContentProps) => {
  const resolvedIcon = icon !== undefined ? icon : (() => {
    const DefaultIcon = DEFAULT_ICONS[type];
    return DefaultIcon ? <DefaultIcon /> : null;
  })();

  return (
    <div className={classnames(s.snackbar, s[`snackbar--${type}`])}>
      {resolvedIcon && <span className={s.icon}>{resolvedIcon}</span>}
      <span className={s.text}>{text}</span>
      {(actionLabel || actionClick) && (
        <button type="button" className={s.action} onClick={actionClick}>
          {actionLabel ?? 'Action'}
        </button>
      )}
    </div>
  );
};

const SnackbarSupportContent = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator?.clipboard?.writeText(text).then(() => setCopied(true));
  };

  return (
    <div className={classnames(s.snackbar, s['snackbar--support'])}>
      <span className={s.icon}>
        <IconWarning />
      </span>
      <span className={s.text}>{text}</span>
      <button type="button" className={s.action} onClick={handleCopy}>
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Snackbar — main function, returns a toast ID (same as toast())
// ---------------------------------------------------------------------------
/**
 * Displays a snackbar notification using react-toastify.
 *
 * @example
 * Snackbar({ type: 'success', text: 'Saved!' });
 * Snackbar({ type: 'support', text: 'Error text' });
 * Snackbar({ type: 'info', text: 'Note', actionLabel: 'Undo', actionClick: handleUndo });
 */
export const Snackbar = ({
  text,
  type = 'neutral',
  icon,
  actionLabel,
  actionClick,
  toastConfig,
}: SnackbarParams) =>
  toast(
    <SnackbarContent
      text={text}
      type={type}
      icon={icon}
      actionLabel={actionLabel}
      actionClick={actionClick}
    />,
    {
      ...toastDefaultConfig,
      ...getPosition(),
      ...toastConfig,
    },
  );

// ---------------------------------------------------------------------------
// SnackbarSupport — dedicated function for service/support toasts
// ---------------------------------------------------------------------------
export const SnackbarSupport = ({ text, toastConfig }: SnackbarSupportParams) =>
  toast(<SnackbarSupportContent text={text} />, {
    ...toastDefaultConfig,
    ...getPosition(),
    ...toastConfig,
  });

// ---------------------------------------------------------------------------
// Notify — backwards-compatible class wrapping Snackbar. Deprecated
// Preserves the original static API used across the apps.
// data.title is intentionally dropped (not part of the new design).
// ---------------------------------------------------------------------------
export class Notify {
  static success = (data: { title?: string; text: string }, customConfig?: ToastOptions) =>
    Snackbar({ type: 'success', text: data.text, toastConfig: customConfig });

  static error = (data: { title?: string; text: string }, customConfig?: ToastOptions) =>
    Snackbar({ type: 'danger', text: data.text, toastConfig: customConfig });

  static errorSupport = (data: { title?: string; text: string }, customConfig?: ToastOptions) =>
    SnackbarSupport({ text: data.text, toastConfig: customConfig });
}

export default Notify;